import { ArrowDownward, ArrowUpward, Delete, DragIndicator, Edit } from '@mui/icons-material';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { config, useSprings } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReorderLayout } from '~/api/layouts/update';
import { useDeleteZone } from '~/api/zones';
import { ROW_TOTAL_HEIGHT, hiddenHeader } from '~/components/data-grid';
import { TableAction } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { assert } from '~/lib/assert';
import { useLayout } from '../../context';
import type { LayoutList__Zone as Zone } from '../../queries/list.generated';

interface CalculateStylesProps {
  active?: boolean;
  currentIndex?: number;
  immediate?: boolean;
  order: number[];
  originalIndex?: number;
  y?: number;
}

const calculateStyles =
  ({
    active = false,
    currentIndex = 0,
    immediate = false,
    order,
    originalIndex = 0,
    y = 0,
  }: CalculateStylesProps) =>
  (index: number) =>
    active && index === originalIndex
      ? {
          y: currentIndex * ROW_TOTAL_HEIGHT + y,
          zIndex: 1,
          immediate: (key: string) => immediate || key === 'zIndex',
          config: (key: string) => (key === 'y' ? config.stiff : config.default),
        }
      : {
          y: order.indexOf(index) * ROW_TOTAL_HEIGHT,
          zIndex: 0,
          immediate,
        };

export const useReorder = (
  layoutId: number,
  zones: ReadonlyArray<Zone>,
  editZone: (zone: Zone) => void,
) => {
  const { t } = useTranslation();
  const [reorder] = useReorderLayout();
  const [deleteZone] = useDeleteZone();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);

  const layout = useLayout();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      reorder: !isSmallAndDown,
      height: !isSmallAndDown,
      left: !isSmallAndDown,
      top: !isSmallAndDown,
      width: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  // Positions of each zone index. State so the Up/Down buttons re-render with it; the drag
  // gesture reads it synchronously between renders, so a ref mirrors it.
  const [order, setOrder] = useState(() => zones.map((_, index) => index));
  const orderRef = useRef(order);
  const applyOrder = useCallback((next: number[]) => {
    orderRef.current = next;
    setOrder(next);
  }, []);

  const [springs, api] = useSprings(zones.length, calculateStyles({ order: orderRef.current }));

  useEffect(() => {
    const serverOrder = zones.map((_, index) => index);
    applyOrder(serverOrder);
    api.start(calculateStyles({ immediate: true, order: serverOrder }));
  }, [api, applyOrder, zones]);

  const bind = useDrag(
    ({ active, args: [originalIndex], movement: [, y] }) => {
      assert(typeof originalIndex === 'number');

      const currentIndex = orderRef.current.indexOf(originalIndex);
      const approximateRow = Math.round((currentIndex * ROW_TOTAL_HEIGHT + y) / ROW_TOTAL_HEIGHT);

      const moveToIndex =
        approximateRow < 0
          ? 0
          : approximateRow > zones.length - 1
          ? zones.length - 1
          : approximateRow;

      const newOrder = [...orderRef.current];
      newOrder.splice(moveToIndex, 0, newOrder.splice(currentIndex, 1)[0]);

      api.start(
        calculateStyles({
          active,
          currentIndex,
          order: newOrder,
          originalIndex,
          y,
        }),
      );

      if (!active) {
        applyOrder(newOrder);
        void reorder({
          variables: {
            layoutId,
            childIds: newOrder.map((x) => zones[x].id),
          },
        });
      }
    },
    { filterTaps: true },
  );

  // Keyboard path for the drag handle: the row at `index` swaps with its neighbour.
  const move = useCallback(
    (index: number, delta: -1 | 1) => {
      const currentIndex = orderRef.current.indexOf(index);
      const moveToIndex = currentIndex + delta;
      if (moveToIndex < 0 || moveToIndex > zones.length - 1) return;

      const newOrder = [...orderRef.current];
      newOrder.splice(moveToIndex, 0, newOrder.splice(currentIndex, 1)[0]);
      applyOrder(newOrder);
      api.start(calculateStyles({ order: newOrder }));
      void reorder({
        variables: {
          layoutId,
          childIds: newOrder.map((x) => zones[x].id),
        },
      });
    },
    [api, applyOrder, layoutId, reorder, zones],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      const zone = zones.find((z) => z.id === id);
      if (!zone) return;
      setZoneToDelete(zone);
      if (!(await confirmDelete())) {
        setZoneToDelete(null);
        return;
      }
      await deleteZone({
        refetchQueries: ['LayoutGet'],
        variables: { id },
      });
      setZoneToDelete(null);
    },
    [confirmDelete, deleteZone, zones],
  );

  const columns: GridColDef<Zone>[] = useMemo(
    () => [
      {
        field: 'reorder',
        ...hiddenHeader(t('common.labels.reorder')),
        width: 25,
        renderCell: ({ api: { getRowIndexRelativeToVisibleRows }, row }) => (
          <Box
            {...bind(getRowIndexRelativeToVisibleRows(row.id))}
            sx={{ display: 'flex', '&:hover': { cursor: 'grab' } }}
          >
            {row.canUpdate.value && <DragIndicator color="secondary" />}
          </Box>
        ),
        sortable: false,
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        sortable: false,
      },
      {
        field: 'width',
        headerName: t('common.labels.width'),
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'height',
        headerName: t('common.labels.height'),
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'left',
        headerName: t('pages.layouts.detail.reorder.columns.left_x'),
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'top',
        headerName: t('pages.layouts.detail.reorder.columns.top_y'),
        minWidth: 100,
        sortable: false,
      },
      {
        align: 'right',
        field: 'actions',
        flex: 1,
        getActions: ({ id, row }) => {
          if (!layout.canUpdate.value) return [];

          const index = zones.indexOf(row);
          const position = order.indexOf(index);

          return [
            <TableAction
              key={`up-${id}`}
              title={t('pages.layouts.detail.reorder.actions.move_zone_up')}
              disabled={!row.canUpdate.value || position <= 0}
              Icon={ArrowUpward}
              onClick={() => move(index, -1)}
            />,
            <TableAction
              key={`down-${id}`}
              title={t('pages.layouts.detail.reorder.actions.move_zone_down')}
              disabled={!row.canUpdate.value || position >= zones.length - 1}
              Icon={ArrowDownward}
              onClick={() => move(index, 1)}
            />,
            <TableAction
              key={`edit-${id}`}
              title={t('pages.layouts.detail.reorder.actions.edit_zone')}
              Icon={Edit}
              onClick={() => {
                editZone(row);
              }}
            />,
            <TableAction
              key={`delete-${id}`}
              title={
                row.canDestroy.value
                  ? t('pages.layouts.detail.reorder.actions.delete_zone')
                  : row.canDestroy.reasons?.fullMessages[0] ?? ''
              }
              disabled={!row.canDestroy.value}
              color="error"
              Icon={Delete}
              onClick={() => handleDelete(row.id)}
            />,
          ];
        },
        sortable: false,
        type: 'actions',
      },
    ],
    [bind, editZone, handleDelete, layout, move, order, t, zones],
  );

  return {
    columns,
    confirmDeleteProps,
    showColumns,
    springs,
    zoneToDelete,
  };
};
